
import React from 'react';
import { Star, Award, Globe, Clock, User } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getAvatarUrl } from '@/utils/storage';
import { getFullName } from '@/utils/name-helpers';
import { getTimezoneDisplayWithOffset } from '@/utils/timezone-detection';
import { Practitioner } from '@/types/practitioner';

interface PractitionerHeaderProps {
  practitioner: Practitioner;
}

const PractitionerHeader: React.FC<PractitionerHeaderProps> = ({ practitioner }) => {
  const {
    avatar_url,
    professional_title,
    years_of_experience,
    languages_spoken = [],
    average_rating = 0,
    total_reviews = 0,
    timezone
  } = practitioner;

  const practitionerName = getFullName(practitioner);
  const formattedAvatarUrl = avatar_url ? getAvatarUrl('practitioner.photo', avatar_url) : '';
  const safeAverageRating = average_rating !== null && average_rating !== undefined ? average_rating : 0;
  const safeLanguagesSpoken = languages_spoken || [];

  return (
    <div className="flex flex-col md:flex-row gap-6">
      <Avatar className="h-32 w-32 mx-auto md:mx-0">
        {formattedAvatarUrl ? (
          <AvatarImage 
            src={formattedAvatarUrl} 
            alt={practitionerName || "Practitioner"}
            crossOrigin="anonymous"
          />
        ) : (
          <AvatarFallback className="bg-amber-100 text-amber-800">
            <User size={48} />
          </AvatarFallback>
        )}
      </Avatar>

      <div className="flex-1 text-center md:text-left">
        <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
          <h1 className="text-3xl font-serif text-earth-900">
            {practitionerName || "Unnamed Practitioner"}
          </h1>
        </div>
        
        <p className="text-xl text-amber-700 font-medium mb-3">
          {professional_title || "Spiritual Practitioner"}
        </p>

        <div className="flex items-center justify-center md:justify-start mb-4">
          <Star fill="currentColor" className="h-5 w-5 text-amber-500 mr-1" />
          <span className="font-medium text-lg">{safeAverageRating.toFixed(1)}</span>
          <span className="text-earth-600 ml-2">({total_reviews} reviews)</span>
        </div>

        {/* Quick Info */}
        <div className="flex flex-wrap gap-4 justify-center md:justify-start text-sm text-earth-600">
          {years_of_experience && (
            <div className="flex items-center">
              <Award className="h-4 w-4 mr-1" />
              <span>{years_of_experience} years experience</span>
            </div>
          )}
          
          {safeLanguagesSpoken.length > 0 && (
            <div className="flex items-center">
              <Globe className="h-4 w-4 mr-1" />
              <span>{safeLanguagesSpoken.join(', ')}</span>
            </div>
          )}

          {timezone && (
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              <span>{getTimezoneDisplayWithOffset(timezone)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PractitionerHeader;
